import { DataButton } from "@sellar-io/components";
import { TableInputContainer } from "../../../../components/tableInputContainer/TableInputContainer";
import { twMerge } from "tailwind-merge";
import { useState } from "react";
import { useShallow } from "zustand/react/shallow";
import { useAllOrdersListStore } from "./AllOrdersListStore";
import {
  InputContainer,
  InputSelect,
  InputOption,
} from "@sellar-io/components";
import {
  FulfillmentFilterType,
  OrderContainsType,
  OrderContainsValues,
  OrderFulfillmentFilterValues,
  OrderListSortType,
  OrderListSortValues,
  OrderPaymentStatusValues,
  OrderPaymentTypeValues,
  OrderStatusesFilterValues,
  PaymentStatusType,
  PaymentTypeType,
} from "./AllOrdersListFilterTypes";
import { Icon, SupplierOnly } from "../../../../components";
import { OrderItemExportButton } from "./AllOrdersList";

import {
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
} from "@headlessui/react";
import { useRemoteFeatureFlags } from "~/hooks/useRemoteFeatureFlags";
import { useSellarCurrentBusiness } from "~/hooks/useSellarCurrentBusiness.shared";

export const AllOrdersListFilter = ({
  onDismiss,
  setClickOutside,
}: {
  onDismiss: () => void;
  setClickOutside?: (value?: boolean) => void;
}) => {
  const state = useAllOrdersListStore(useShallow((state) => state));
  const cardWithDepositEnabled =
    useRemoteFeatureFlags().isActive("cardWithDeposit");
  const { business } = useSellarCurrentBusiness();
  const isSupplier = business?.type === "supplier";

  const [deliveryStatus, setDeliveryStatus] = useState(state.deliveryStatus);
  const [orderContains, setOrderContains] = useState(state.orderContains);
  const [orderStatuses, setOrderStatuses] = useState(state.orderStatuses);
  const [paymentStatus, setPaymentStatus] = useState(state.paymentStatus);
  const [paymentType, setPaymentType] = useState(state.paymentType);
  const [sort, setSort] = useState(state.sort);

  const filtersAlreadyApplied =
    state.deliveryStatus !== "" ||
    state.orderContains !== "" ||
    state.orderStatuses.length > 0 ||
    state.paymentStatus !== "" ||
    state.paymentType !== "" ||
    state.search !== "" ||
    state.sort !== "created_at_desc";

  const disabled =
    deliveryStatus === state.deliveryStatus &&
    orderContains === state.orderContains &&
    orderStatuses == state.orderStatuses &&
    paymentStatus === state.paymentStatus &&
    paymentType === state.paymentType &&
    sort === state.sort;

  const onApply = (reset: boolean) => {
    if (reset) {
      useAllOrdersListStore.getState().reset();
    } else {
      useAllOrdersListStore.getState().setState({
        ...state,
        deliveryStatus,
        orderContains,
        orderStatuses,
        paymentStatus,
        paymentType,
        sort,
      });
    }
    if (setClickOutside) setClickOutside(true);
    onDismiss();
  };

  const truncate = (text: string) => {
    return text.length > 19 ? `${text.slice(0, 17)}...` : text;
  };

  return (
    <>
      <div className={twMerge("w-full px-4 pt-2")}>
        <span className="font-semibold">Sort orders</span>
      </div>
      <div className="w-full border-b border-zinc-300 p-4">
        <InputContainer>
          <InputSelect
            value={sort}
            onChange={(e) => {
              setSort(e.target.value as OrderListSortType);
            }}
          >
            {Object.values(OrderListSortValues).map((value) => (
              <InputOption key={value.key} value={value.key}>
                {value.name}
              </InputOption>
            ))}
          </InputSelect>
        </InputContainer>
      </div>
      <div className={twMerge("w-full px-4 pt-2")}>
        <span className="font-semibold">Filter orders</span>
      </div>
      <div className="w-full p-4">
        <div className="flex flex-col gap-3">
          <div className="flex w-full items-start justify-between">
            <span className="mt-2">Order status</span>
            <div className="ml-4 flex w-[55%] flex-col gap-2">
              <Listbox
                value={orderStatuses}
                onChange={setOrderStatuses}
                multiple
              >
                <ListboxButton
                  className={
                    "relative flex w-full items-center overflow-hidden rounded-lg border border-solid bg-white px-4 py-2 pr-6 text-left text-sm font-medium text-ellipsis whitespace-nowrap shadow-xs"
                  }
                  onClick={() => {
                    if (setClickOutside) setClickOutside(false);
                  }}
                >
                  {orderStatuses.length
                    ? truncate(
                        orderStatuses
                          .map(
                            (status) => OrderStatusesFilterValues[status].name,
                          )
                          .join(", "),
                      )
                    : "All statuses"}
                  <Icon
                    icon="CHEVRON_DOWN"
                    size={0.8}
                    className="absolute right-2"
                  />
                </ListboxButton>
                <ListboxOptions
                  className={
                    "mt-1 w-[190.3px] rounded-lg border border-solid bg-white p-2 px-4 text-left text-sm shadow-xs"
                  }
                  anchor="bottom start"
                >
                  <ListboxOption
                    key={"x1"}
                    value={false}
                    disabled={true}
                    className="flex w-full cursor-pointer items-center gap-1 rounded-sm py-2 text-zinc-500 active:bg-zinc-50 data-focus:bg-blue-100"
                  >
                    Select multiple...
                  </ListboxOption>
                  {Object.values(OrderStatusesFilterValues).map(
                    (orderStatus, i) => (
                      <ListboxOption
                        key={i}
                        value={orderStatus.key}
                        className="flex w-full cursor-pointer items-center gap-1 rounded-sm py-2 active:bg-zinc-50 data-focus:bg-blue-100"
                      >
                        {orderStatuses.includes(orderStatus.key) ? (
                          <Icon icon="CHECK" size={0.7} />
                        ) : (
                          <span className="w-[16.8px]" />
                        )}
                        {orderStatus.name}
                      </ListboxOption>
                    ),
                  )}
                </ListboxOptions>
              </Listbox>
            </div>
          </div>

          <div className="flex w-full items-center justify-between">
            <span>Order contains</span>
            <TableInputContainer className="ml-4 w-[55%]">
              <InputSelect
                defaultValue={orderContains}
                onChange={(e) =>
                  setOrderContains(e.target.value as OrderContainsType)
                }
                className="text-sm font-medium"
              >
                {Object.values(OrderContainsValues).map((value) => (
                  <InputOption key={value.key} value={value.key}>
                    {value.name}
                  </InputOption>
                ))}
              </InputSelect>
            </TableInputContainer>
          </div>

          <div className="flex w-full items-center justify-between">
            <span>Delivery type</span>
            <TableInputContainer className="ml-4 w-[55%]">
              <InputSelect
                defaultValue={deliveryStatus}
                onChange={(e) =>
                  setDeliveryStatus(e.target.value as FulfillmentFilterType)
                }
                className="text-sm font-medium"
              >
                {Object.values(OrderFulfillmentFilterValues).map((value) => (
                  <InputOption key={value.key} value={value.key}>
                    {value.name}
                  </InputOption>
                ))}
              </InputSelect>
            </TableInputContainer>
          </div>

          <div className="flex w-full items-center justify-between">
            <span>Payment status</span>
            <TableInputContainer className="ml-4 w-[55%]">
              <InputSelect
                defaultValue={paymentStatus}
                onChange={(e) =>
                  setPaymentStatus(e.target.value as PaymentStatusType)
                }
                className="text-sm font-medium"
              >
                {Object.values(OrderPaymentStatusValues).map((value) => (
                  <InputOption key={value.key} value={value.key}>
                    {value.name}
                  </InputOption>
                ))}
              </InputSelect>
            </TableInputContainer>
          </div>

          <div className="flex w-full items-center justify-between">
            <span>Payment type</span>
            <TableInputContainer className="ml-4 w-[55%]">
              <InputSelect
                defaultValue={paymentType}
                onChange={(e) =>
                  setPaymentType(e.target.value as PaymentTypeType)
                }
                className="text-sm font-medium"
              >
                {Object.values(OrderPaymentTypeValues)
                  .filter((value) => {
                    if (
                      // We only want the cardWithDeposit option for suppliers with the feature enabled, but always visible for retailers
                      value.key === "cardPaymentWithDeposit" &&
                      isSupplier &&
                      !cardWithDepositEnabled
                    ) {
                      return false;
                    }
                    return true;
                  })
                  .map((value) => {
                    return (
                      <InputOption key={value.key} value={value.key}>
                        {value.name}
                      </InputOption>
                    );
                  })}
              </InputSelect>
            </TableInputContainer>
          </div>
        </div>
      </div>
      <div className="flex w-full items-center p-4">
        <DataButton
          onClick={() => onApply(false)}
          border={true}
          disabled={disabled}
        >
          {filtersAlreadyApplied ? "Update" : "Apply"}
        </DataButton>
        {filtersAlreadyApplied && (
          <DataButton color="transparent" onClick={() => onApply(true)}>
            Clear all
          </DataButton>
        )}
      </div>
      <SupplierOnly>
        <div className="w-full border-y border-zinc-300 p-4">
          <OrderItemExportButton
            onClick={() => {
              if (setClickOutside) setClickOutside();
            }}
          />
        </div>
      </SupplierOnly>
    </>
  );
};
